import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import HeaderSection from "./HeaderSection";
import UserTable from "./UserTable";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import AddUserDialog from "../AddUserDialog";
import EditUserDialog from "./EditUserDialog";
import ViewUserDialog from "../ViewUserDialog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchPartners, changePartnerStatus } from "./api";
import { useLocation } from "react-router-dom";

const CommonPartner = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [addUserOpen, setAddUserOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [tablemsg, setTablemsg] = useState("");
    const location = useLocation();
    const partnerName = location.state?.partnerName || "Default Name";
    const roleId = location.state?.roleId || "Default Role";

    const lastFetchedRoleId = useRef(null);

    const fetchData = async (page = currentPage, searchQuery) => {
        if (!roleId) return;
        setLoading(true);
        try {
            const response = await fetchPartners(page, pageSize, roleId, setTotalRecords, searchQuery, setTotalPages);
            console.log(response.statusCodeValue);
            if (response?.statusCodeValue === 204) {
                console.log(response?.body?.message || "No Data Found!");
                toast.info(response?.body?.message || "No Data Found!", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored",
                });
                const message = response?.body?.message || "No Data Found!";
                setTablemsg(message); // Set message for table
                setPartners([]); // Ensure no stale data remains in partners
            } else {
                setTablemsg(""); // Reset message when data is available
                setPartners(response.body.data);
            }
        } catch (err) {
            setError("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (lastFetchedRoleId.current !== roleId) {
            setCurrentPage(0);
            fetchData(0);
            lastFetchedRoleId.current = roleId;
        } else {
            fetchData();
        }
    }, [roleId, currentPage, pageSize]);

    const handleChangeStatus = async (userId, currentStatus, roleId) => {
        // Optimistically update the UI
        setPartners((prevPartners) =>
            prevPartners.map((p) =>
                p.userid === userId ? { ...p, status: currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE" } : p
            )
        );
    
        try {
            const response = await changePartnerStatus(userId, currentStatus, roleId);
    
            if (!toast) {
                console.error("Toast is undefined! Ensure ToastContainer is present.");
                return;
            }
    
            if (response?.statusCodeValue === 200 && response?.body?.message === "Status updated successfully") {
                toast.success("Status updated successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored",
                });

            } else if (response?.statusCodeValue === 302) {
                toast.info("No IP addresses found for your organization.", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    theme: "colored",
                });
            } else {
                toast.error("Failed to update status: Unexpected API response", {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "colored",
                });
                throw new Error("Unexpected API response");
            }
        } catch (error) {
            // Rollback UI state if API call fails
            setPartners((prevPartners) =>
                prevPartners.map((p) =>
                    p.userid === userId ? { ...p, status: currentStatus } : p
                )
            );
    
            toast.error("Failed to update status!", {
                position: "top-right",
                autoClose: 5000,
                theme: "colored",
            });
            console.error("Failed to update status:", error);
        }
    };
    
    useEffect(() => {
        if (searchQuery.trim() === "") {
            fetchData(0); // Fetch data without search when query is empty
        }
    }, [searchQuery]);

    const handleSearch = () => {
        console.log(searchQuery);
        fetchData(0, searchQuery);
    };
    
    const handlePageChange = (event, value) => {
        if (value - 1 !== currentPage) {
            setCurrentPage(value - 1);
        }
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(0);
    };

    return (
        <>
            <Box sx={{ padding: 3, marginTop: "5%", backgroundColor: "#F4F5FA", height: "100vh" }}>
                <HeaderSection
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    handleSearch={handleSearch}
                    handleAddUserOpen={() => setAddUserOpen(true)}
                    partnerName={partnerName}
                    roleId={roleId}
                    fetchData={fetchData}
                />
                <UserTable
                    roleId={roleId}
                    tablemsg={tablemsg}  // Pass the table message
                    partnerName={partnerName}
                    partners={partners}
                    handleViewOpen={(user) => {
                        setSelectedUser(user);
                        setViewOpen(true);
                    }}
                    handleEditOpen={(user) => {
                        setSelectedUser(user);
                        setEditOpen(true);
                    }}
                    handleOpenModal={(user) => {
                        setSelectedUser(user);
                        setOpenModal(true);
                    }}
                    handleStatusToggle={handleChangeStatus}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    totalRecords={totalRecords}
                    handlePageChange={handlePageChange}
                    handlePageSizeChange={handlePageSizeChange}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                />
                {loading && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginTop: 3,
                        }}
                    >
                        <CircularProgress />
                        <Typography sx={{ marginLeft: 2 }}>Loading data...</Typography>
                    </Box>
                )}
            </Box>

            {/* <DeleteConfirmationModal open={openModal} handleClose={() => setOpenModal(false)} user={selectedUser} /> */}
            <DeleteConfirmationModal
                open={openModal}
                handleClose={() => setOpenModal(false)}
                user={selectedUser}
                onDeleteSuccess={() => fetchData()}
            />

            <AddUserDialog open={addUserOpen} handleClose={() => setAddUserOpen(false)} fetchData={fetchData} />
            <EditUserDialog
                open={editOpen}
                handleClose={() => {
                    setEditOpen(false);
                    fetchData(0); // Fetch updated data after closing edit modal
                }}
                user={selectedUser}
                handleSave={(updatedUser) => {
                    setPartners((prevPartners) =>
                        prevPartners.map((p) => (p.userid === updatedUser.userid ? updatedUser : p))
                    );
                }}
            />
            <ViewUserDialog open={viewOpen} handleClose={() => setViewOpen(false)} user={selectedUser} />
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
};

export default CommonPartner;
